import React from "react";
import clsx from "clsx";
import { makeStyles, useTheme } from "@mui/styles";
import { Slider, IconButton } from "@mui/material";
import {
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Fullscreen as FullscreenIcon,
} from "@mui/icons-material";
import { debounce } from "lodash";
import dayjs from "dayjs";

const useStyles = makeStyles((theme) => ({
  html5Video: {
    maxWidth: "100%",
  },
  controls: {
    display: "flex",
    alignItems: "center",
    background: theme.palette.grey["A400"],
    padding: theme.spacing(1),
    color: theme.palette.getContrastText(theme.palette.grey["A400"]),
    marginTop: theme.spacing(1),
  },
  controlButton: {
    color: theme.palette.getContrastText(theme.palette.grey["A400"]),
  },
  flexGrow: {
    flexGrow: 1,
  },
  playContainer: {
    marginRight: theme.spacing(1),
  },
  sliderContainer: {
    marginRight: theme.spacing(2),
  },
  videoContent: {
    display: "grid",
    gridGap: theme.spacing(1),
    width: "100%",
    height: "100%",
  },
  videoContent1i: {
    [theme.breakpoints.up("sm")]: {
      gridTemplateColumns: "repeat(1, 1fr)",
    },
  },
  videoContent2i: {
    [theme.breakpoints.up("sm")]: {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
  },
  videoContent4i: {
    [theme.breakpoints.up("sm")]: {
      gridTemplateColumns: "repeat(2, 1fr)",
      gridTemplateRows: "repeat(2, 1fr)",
    },
  },
  videoContentGrid: {
    [theme.breakpoints.up("sm")]: {
      gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    },
  },
  video: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
  fullscreenBtn: {
    position: "absolute",
    bottom: 8,
    right: 8,
    background: "rgba(0,0,0,0.5)",
    "&:hover": { background: "rgba(0,0,0,0.8)" },
  },
  videoWrapper: {
    position: "relative",
  },
}));

const FootageGrid = ({ footage, safetyEvent }) => {
  const classes = useStyles();
  const theme = useTheme();
  const videoRefs = React.useRef([]);

  const cameraLength = footage?.length || 0;
  const [markerTime, setMarkerTime] = React.useState(null);
  const [playing, setPlaying] = React.useState(true);
  const playingRef = React.useRef(true);
  const [sliderState, setSliderState] = React.useState({
    currentTime: 0,
    duration: 0,
  });

  const { firstStartTime, offsets } = React.useMemo(() => {
    const starts = (footage || []).map(({ startTime }) =>
      dayjs(startTime).valueOf(),
    );
    const first = Math.min(...starts);
    return {
      firstStartTime: Number.isFinite(first) ? first : null,
      offsets: starts.map((start) => (start - first) / 1000 || 0),
    };
  }, [footage]);

  React.useEffect(() => {
    if (!safetyEvent?.occurredAt || firstStartTime === null) return;

    const eventTime = dayjs(safetyEvent.occurredAt);
    const diff = eventTime.diff(dayjs(firstStartTime), "second");
    if (diff >= 0) setMarkerTime(diff);
  }, [safetyEvent, firstStartTime]);

  const timelineDuration = React.useCallback(
    () =>
      Math.max(
        0,
        ...videoRefs.current.map((vid, idx) =>
          vid && Number.isFinite(vid.duration)
            ? offsets[idx] + vid.duration
            : 0,
        ),
      ),
    [offsets],
  );

  const syncVideos = React.useCallback(
    (time, play) => {
      videoRefs.current.forEach((vid, idx) => {
        if (!vid) return;
        const target = time - offsets[idx];
        const clamped = Math.min(Math.max(target, 0), vid.duration || Infinity);
        if (
          target < 0
            ? vid.currentTime > 0
            : Math.abs(vid.currentTime - clamped) > 0.5
        )
          vid.currentTime = clamped;

        const shouldPlay = play && target >= 0 && !(target >= vid.duration);
        if (shouldPlay && vid.paused) vid.play();
        else if (!shouldPlay && !vid.paused) vid.pause();
      });
    },
    [offsets],
  );

  const handlePlayButtonClick = () => {
    playingRef.current = true;
    setPlaying(true);
    const atEnd = sliderState.currentTime >= sliderState.duration - 0.1;
    syncVideos(atEnd ? 0 : sliderState.currentTime, true);
  };

  const handlePauseButtonClick = () => {
    playingRef.current = false;
    setPlaying(false);
    syncVideos(sliderState.currentTime, false);
  };

  const debouncedSeek = React.useMemo(
    () => debounce((seekTo) => syncVideos(seekTo, playingRef.current), 200),
    [syncVideos],
  );

  const handleSliderChange = (_, newValue) => {
    setSliderState((prev) => ({ ...prev, currentTime: newValue }));
    debouncedSeek(newValue);
  };

  React.useEffect(() => {
    const videos = videoRefs.current.slice(0, footage.length);
    const byOffset = videos
      .map((_, idx) => idx)
      .sort((a, b) => offsets[a] - offsets[b]);

    const onTimeUpdate = (ev) => {
      const leader = byOffset.find((idx) => videos[idx] && !videos[idx].paused);
      if (leader === undefined || videos[leader] !== ev.target) return;

      const time = ev.target.currentTime + offsets[leader];
      setSliderState({ currentTime: time, duration: timelineDuration() });
      syncVideos(time, playingRef.current);
    };

    const onEnded = () => {
      if (videos.every((vid) => !vid || vid.paused)) {
        playingRef.current = false;
        setPlaying(false);
      }
    };

    videos.forEach((vid) => {
      vid?.addEventListener("timeupdate", onTimeUpdate);
      vid?.addEventListener("ended", onEnded);
    });
    return () =>
      videos.forEach((vid) => {
        vid?.removeEventListener("timeupdate", onTimeUpdate);
        vid?.removeEventListener("ended", onEnded);
      });
  }, [footage, offsets, syncVideos, timelineDuration]);

  const handleFullscreenToggle = (idx) => {
    const video = videoRefs.current[idx];
    if (!video) return;

    if (!document.fullscreenElement) {
      video.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  return (
    <div>
      {/* Video Grid */}
      <div
        className={clsx(classes.videoContent, {
          [classes.videoContent1i]: cameraLength === 1,
          [classes.videoContent2i]: cameraLength === 2,
          [classes.videoContent4i]: cameraLength >= 3 && cameraLength <= 4,
          [classes.videoContentGrid]: cameraLength > 4,
        })}
      >
        {footage.map(({ signedUrl }, idx) => (
          <div key={idx} className={classes.videoWrapper}>
            <video
              ref={(el) => (videoRefs.current[idx] = el)}
              className={classes.video}
              playsInline
              poster="/live-video-poster.svg"
              muted
              autoPlay
            >
              <source src={signedUrl} />
              Sorry, your browser does not support embedded videos.
            </video>
            <IconButton
              className={classes.fullscreenBtn}
              onClick={() => handleFullscreenToggle(idx)}
              size="small"
            >
              <FullscreenIcon style={{ color: "#fff" }} />
            </IconButton>
          </div>
        ))}
      </div>

      {/* Controls */}
      {videoRefs.current[0] && (
        <div className={classes.controls}>
          <div className={classes.playContainer}>
            {!playing ? (
              <IconButton
                className={classes.controlButton}
                onClick={handlePlayButtonClick}
                size="large"
              >
                <PlayIcon />
              </IconButton>
            ) : (
              <IconButton
                className={classes.controlButton}
                onClick={handlePauseButtonClick}
                size="large"
              >
                <PauseIcon />
              </IconButton>
            )}
          </div>

          <div className={clsx(classes.flexGrow, classes.sliderContainer)}>
            <Slider
              value={sliderState.currentTime}
              max={sliderState.duration}
              step={0.1}
              onChange={handleSliderChange}
              aria-labelledby="continuous-slider"
              marks={
                markerTime
                  ? [
                      { value: Math.max(markerTime - 10, 0) },
                      { value: markerTime },
                    ]
                  : []
              }
              sx={{
                "& .MuiSlider-mark": {
                  backgroundColor: theme.palette.warning.main,
                  height: 16,
                  width: 16,
                  borderRadius: "50%",
                  top: "50%",
                  transform: "translateY(-50%)",
                },
              }}
            />
          </div>

          <div>
            {dayjs.unix(Math.round(sliderState.currentTime)).format("mm:ss")} /{" "}
            {dayjs.unix(Math.round(sliderState.duration)).format("mm:ss")}
          </div>
        </div>
      )}
    </div>
  );
};

export default FootageGrid;
